import { I18n } from "@assets/strings";
import React, { useRef } from "react";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { useCurrentProfile, useIsPerson } from "@domain/currentProfile/hooks";
import { Quiz, QuizParticipant } from "@domain/lms/quiz/quiz";
import { getDisplayedAuthor } from "@domain/lms/utils";
import { PaginatedDataResult, useIsMobile } from "@utils/hooks";
import { ClosableModal } from "@ui/modals/closableModal";
import { theme } from "@ui/styles/theme";
import { desktop, mobile } from "@ui/styles/utils";
import { Secondary } from "@ui/components/containers/textBlocks";
import { InfiniteScroll } from "@ui/components/infiniteScroll";
import { Row, Stack } from "@ui/components/layout";
import { FollowButton } from "@ui/components/profile/followButton";
import { UserView } from "@ui/components/profile/userView";

interface QuizParticipantModalListProps extends React.HTMLAttributes<HTMLDivElement> {
	quiz: Quiz;
	data: PaginatedDataResult<QuizParticipant>;
	close: () => void;
}

const caclScore = (score: number): number => {
	return (score * 360) / 100;
};

const rangeColor = (score: number): string => {
	if (score >= 0 && score <= 39) return theme.colors.primary;
	if (score >= 40 && score <= 79) return theme.colors.thirded;
	return theme.colors.winterGreen;
};

export const QuizParticipantModalList: React.FC<QuizParticipantModalListProps> = ({
	data,
	close,
	quiz,
	...htmlProps
}) => {
	const displayedAuthor = getDisplayedAuthor(quiz);
	const isAuthor = useIsPerson({ id: displayedAuthor?.id });
	const currentProfile = useCurrentProfile();

	const { result: participants, loading, moreLoading, fetchMore, totalElements } = data;
	const contentRef = useRef<HTMLDivElement>(null);
	const isMobile = useIsMobile();

	return loading ? (
		<ClipLoader />
	) : (
		<Container
			close={close}
			title={I18n.t("quiz.infos.participants", { count: quiz.participants.count })}
			{...htmlProps}
		>
			<Content ref={contentRef}>
				{!totalElements && !loading ? (
					<Secondary>{I18n.t("quiz.infos.participants", { count: quiz.participants.count })}</Secondary>
				) : (
					<InfiniteScroll onEndReached={fetchMore} scrollingElement={contentRef} loading={loading || moreLoading}>
						<Stack gap={10}>
							{participants.map((participant) =>
								participant && participant.id ? (
									<Row key={participant.id} justify="space-between" align="center">
										<BigUserView
											key={participant.id}
											user={participant}
											showDescription
											pictureSize={"M"}
											onClick={close}
											renderBottomAccessory={
												!isAuthor && isMobile && currentProfile?.id !== participant.id
													? () => <StyledFollowButton profile={participant} />
													: undefined
											}
										/>
										{isAuthor && (
											<Score pourcentage={caclScore(participant.score)} rangeColor={rangeColor(participant.score)}>
												<span>{participant.score}%</span>
											</Score>
										)}
										{!isMobile && !isAuthor && currentProfile?.id !== participant.id && (
											<FollowButton profile={participant} />
										)}
									</Row>
								) : undefined
							)}
						</Stack>
					</InfiniteScroll>
				)}
				{(loading || moreLoading) && <ClipLoader />}
			</Content>
		</Container>
	);
};

const Container = styled(ClosableModal)`
	padding: 20px 0 0;

	.modal-title {
		margin-left: 20px;
	}
`;

const Content = styled.div`
	padding: 20px;
	border-top: 1px solid ${({ theme }) => theme.colors.lightergray};
	margin-top: 20px;
	max-height: 370px;
	overflow: auto;
`;

const BigUserView = styled(UserView)`
	${({ theme }) => theme.textStyles.primary};
	font-weight: bold;
	flex-shrink: 1;

	.profile-picture {
		margin-right: 8px;
	}

	@media ${desktop} {
		margin-right: 30px;
		width: 50%;
	}

	width: 80%;
`;

const Score = styled.div.withConfig({
	shouldForwardProp: (prop) => !["pourcentage", "rangeColor"].includes(prop),
})<{ pourcentage: number; rangeColor: string }>`
	position: relative;
	width: 50px;
	height: 50px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 0.938rem;
	line-height: 20px;
	background: ${({ theme, pourcentage, rangeColor }) =>
		`conic-gradient(${rangeColor} ${pourcentage}deg, ${theme.colors.lightergray} 0deg);`}

	::before {
		content: "";
		position: absolute;
		width: 46px;
		height: 46px;
		border-radius: 50%;
		background: #fff;
	}

	> span {
		position: absolute;
	}
`;

const StyledFollowButton = styled(FollowButton)`
	@media ${mobile} {
		margin-top: 10px;
	}
`;
